'use client';

import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';

export type RegistrationStep =
  | 'button_clicked'
  | 'ticket_selected'
  | 'custom_fields_shown'
  | 'profile_modal_shown'
  | 'payment_started';

interface TicketSelection {
  ticketId: string;
  quantity: number;
  price: number;
  name: string;
}

interface IntentData {
  content_id: string;
  content_title: string;
  content_type: 'event' | 'course';
  last_step: RegistrationStep;
  ticket_selections?: TicketSelection[];
  total_price?: number;
}

const ENDPOINT = '/api/registration-intents';

/**
 * These writes go through the API route rather than the browser Supabase
 * client on purpose. The direct client write was subject to RLS with whatever
 * token the tab happened to hold: when that token was stale the insert was
 * rejected, the error was swallowed, and the abandoned registration vanished –
 * no intent row, so no follow-up email (2026-08-28 support case). The route
 * writes under the server-verified session and the retry wrapper refreshes a
 * 401 once before giving up.
 *
 * All three helpers stay best-effort: recovery telemetry must never block or
 * break the registration the user is in the middle of.
 */
async function postIntent(body: Record<string, unknown>, context: string): Promise<void> {
  try {
    const response = await fetchWithAuthRetry(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.error(`Failed to ${context}:`, response.status);
    }
  } catch (error) {
    console.error(`Failed to ${context}:`, error);
  }
}

/**
 * Create or update a registration intent
 * This tracks that a user has started the registration process
 */
export async function upsertRegistrationIntent(
  _userId: string,
  data: IntentData
): Promise<void> {
  await postIntent(
    {
      content_id: data.content_id,
      content_title: data.content_title,
      content_type: data.content_type,
      last_step: data.last_step,
      ticket_selections: data.ticket_selections ?? null,
      total_price: data.total_price ?? null,
    },
    'upsert registration intent'
  );
}

/**
 * Delete a registration intent (user completed registration)
 */
export async function deleteRegistrationIntent(
  _userId: string,
  contentId: string
): Promise<void> {
  try {
    const response = await fetchWithAuthRetry(
      `${ENDPOINT}?content_id=${encodeURIComponent(contentId)}`,
      { method: 'DELETE' }
    );

    if (!response.ok) {
      console.error('Failed to delete registration intent:', response.status);
    }
  } catch (error) {
    console.error('Failed to delete registration intent:', error);
  }
}

/**
 * Update just the step of an existing intent
 */
export async function updateIntentStep(
  _userId: string,
  contentId: string,
  step: RegistrationStep,
  extra?: { ticket_selections?: TicketSelection[]; total_price?: number }
): Promise<void> {
  await postIntent(
    {
      content_id: contentId,
      last_step: step,
      ...(extra?.ticket_selections !== undefined && { ticket_selections: extra.ticket_selections }),
      ...(extra?.total_price !== undefined && { total_price: extra.total_price }),
    },
    'update registration intent step'
  );
}
